import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Add, Save, Delete, Edit, CloudUpload } from '@mui/icons-material';
import { homePopupService, extractApiDataList } from '../services/api';
import { getAssetUrl } from '../config';

const emptyForm = {
  title: '',
  content: '',
  button_text: '',
  button_link: '',
  display_order: '0',
  is_active: '1',
};

const HomePopupsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [popups, setPopups] = useState([]);
  const [newRow, setNewRow] = useState(emptyForm);
  const [newImage, setNewImage] = useState(null);
  const [newPreview, setNewPreview] = useState('');
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyForm);
  const [editImage, setEditImage] = useState(null);
  const [editPreview, setEditPreview] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await homePopupService.getAll({ include_inactive: 1 });
      setPopups(extractApiDataList(res));
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load popups';
      setLoadError(msg);
      setPopups([]);
      setToast({ open: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPickImage = (file, setFile, setPreview) => {
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result || '');
    reader.readAsDataURL(file);
  };

  const buildFormData = (row, imageFile) => {
    const fd = new FormData();
    if (imageFile) fd.append('image', imageFile);
    fd.append('title', row.title || '');
    fd.append('content', row.content || '');
    fd.append('button_text', row.button_text || '');
    fd.append('button_link', row.button_link || '');
    fd.append('display_order', String(row.display_order === '' ? 0 : row.display_order));
    fd.append('is_active', row.is_active === '1' ? '1' : '0');
    return fd;
  };

  const hasContent = (row, imageFile) =>
    Boolean((row.title || '').trim() || (row.content || '').trim() || imageFile);

  const handleAdd = async () => {
    if (!hasContent(newRow, newImage)) {
      setToast({ open: true, message: 'Add an image, a title, or popup content', type: 'error' });
      return;
    }
    try {
      await homePopupService.create(buildFormData(newRow, newImage));
      setNewRow(emptyForm);
      setNewImage(null);
      setNewPreview('');
      setToast({ open: true, message: 'Popup added', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditImage(null);
    setEditPreview(row.image_url ? getAssetUrl(row.image_url) : '');
    setEditRow({
      title: row.title || '',
      content: row.content || '',
      button_text: row.button_text || '',
      button_link: row.button_link || '',
      display_order: String(row.display_order ?? 0),
      is_active: row.is_active === 0 || row.is_active === false ? '0' : '1',
    });
  };

  const handleSaveEdit = async () => {
    if (!editId) return;
    const existing = popups.find((row) => row.id === editId);
    if (!hasContent(editRow, editImage) && !existing?.image_url) {
      setToast({ open: true, message: 'Add an image, a title, or popup content', type: 'error' });
      return;
    }
    try {
      await homePopupService.update(editId, buildFormData(editRow, editImage));
      setEditId(null);
      setEditImage(null);
      setEditPreview('');
      setToast({ open: true, message: 'Popup updated', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this popup?')) return;
    try {
      await homePopupService.delete(id);
      setToast({ open: true, message: 'Popup deleted', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Home Page Popup
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          The active popup appears on the public home page with a close button. Only one popup can be active at a time. Visitors who close it will not see it again until you update it.
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Add popup
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                Upload image
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => onPickImage(e.target.files?.[0], setNewImage, setNewPreview)}
                />
              </Button>
              {newPreview && (
                <Box component="img" src={newPreview} alt="Preview" sx={{ mt: 1, width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 1 }} />
              )}
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Title" value={newRow.title} onChange={(e) => setNewRow((s) => ({ ...s, title: e.target.value }))} sx={{ mb: 2 }} />
              <TextField
                fullWidth
                size="small"
                label="Content"
                multiline
                minRows={3}
                value={newRow.content}
                onChange={(e) => setNewRow((s) => ({ ...s, content: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Button text" value={newRow.button_text} onChange={(e) => setNewRow((s) => ({ ...s, button_text: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="Button link" value={newRow.button_link} onChange={(e) => setNewRow((s) => ({ ...s, button_link: e.target.value }))} helperText="e.g. /campaigns or https://..." />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Order" type="number" value={newRow.display_order} onChange={(e) => setNewRow((s) => ({ ...s, display_order: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" select label="Active" value={newRow.is_active} onChange={(e) => setNewRow((s) => ({ ...s, is_active: e.target.value }))} SelectProps={{ native: true }}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ backgroundColor: '#2c5f2d' }}>
                Add popup
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                <TableRow>
                  <TableCell>Image</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Button</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {popups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#667085' }}>
                      No popups yet. Add one above.
                    </TableCell>
                  </TableRow>
                ) : (
                  popups.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {editId === row.id ? (
                          <Box>
                            <Button size="small" component="label" startIcon={<CloudUpload />}>
                              Change image
                              <input type="file" hidden accept="image/*" onChange={(e) => onPickImage(e.target.files?.[0], setEditImage, setEditPreview)} />
                            </Button>
                            {editPreview && <Avatar variant="rounded" src={editPreview} sx={{ mt: 1, width: 120, height: 72 }} />}
                          </Box>
                        ) : row.image_url ? (
                          <Avatar variant="rounded" src={getAssetUrl(row.image_url)} sx={{ width: 120, height: 72 }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#667085' }}>No image</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        {editId === row.id ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField size="small" label="Title" value={editRow.title} onChange={(e) => setEditRow((s) => ({ ...s, title: e.target.value }))} />
                            <TextField size="small" label="Content" multiline minRows={3} value={editRow.content} onChange={(e) => setEditRow((s) => ({ ...s, content: e.target.value }))} />
                          </Box>
                        ) : (
                          <Box>
                            {row.title && (
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {row.title}
                              </Typography>
                            )}
                            {row.content && (
                              <Typography variant="caption" sx={{ color: '#667085', display: 'block', whiteSpace: 'pre-wrap' }}>
                                {row.content}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField size="small" label="Button text" value={editRow.button_text} onChange={(e) => setEditRow((s) => ({ ...s, button_text: e.target.value }))} />
                            <TextField size="small" label="Link" value={editRow.button_link} onChange={(e) => setEditRow((s) => ({ ...s, button_link: e.target.value }))} />
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2">{row.button_text || '—'}</Typography>
                            <Typography variant="caption" sx={{ color: '#667085' }}>
                              {row.button_link}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" type="number" value={editRow.display_order} onChange={(e) => setEditRow((s) => ({ ...s, display_order: e.target.value }))} />
                        ) : (
                          row.display_order
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" select value={editRow.is_active} onChange={(e) => setEditRow((s) => ({ ...s, is_active: e.target.value }))} SelectProps={{ native: true }}>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                          </TextField>
                        ) : (
                          <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" color={row.is_active ? 'success' : 'default'} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editId === row.id ? (
                          <>
                            <IconButton size="small" color="primary" onClick={handleSaveEdit} aria-label="Save popup">
                              <Save fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setEditId(null)} aria-label="Cancel edit">
                              ×
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton size="small" onClick={() => startEdit(row)} aria-label="Edit popup">
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} aria-label="Delete popup">
                              <Delete fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePopupsPage;
